import { useMemo } from 'react';
import type { Selection } from 'react-aria-components';
import { Header, Section } from 'react-aria-components';
import { Building2, Monitor } from 'lucide-react';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import {
  ListBox,
  ListBoxItem,
  ListBoxItemContent
} from 'v5/design-system/ListBox';
import { cn } from 'v5/platform/dom/cn';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import {
  buildFeatureFlagScopeSections,
  filterFeatureFlagScopeSections,
  isScopeDirty,
  type FeatureFlagScopeKey
} from './buildFeatureFlagScopeCatalog';
import { FeatureFlagGroup } from 'v5/services/front/FeatureManagementService/types';

export interface FeatureFlagScopeCatalogProps {
  groups: FeatureFlagGroup[];
  workspaceColumns: Array<{ id: number; name: string }>;
  selectedScopeKey: FeatureFlagScopeKey | null;
  onSelect: (scopeKey: FeatureFlagScopeKey) => void;
  search: string;
  changedLegacyFlagIds: readonly string[];
  changedFrontendFlagIds: readonly string[];
  changedWorkspaceFlagIds: Readonly<Record<number, readonly string[]>>;
}

export function FeatureFlagScopeCatalog({
  groups,
  workspaceColumns,
  selectedScopeKey,
  onSelect,
  search,
  changedLegacyFlagIds,
  changedFrontendFlagIds,
  changedWorkspaceFlagIds
}: FeatureFlagScopeCatalogProps) {
  const dirty = useMemo(
    () => ({
      changedLegacyFlagIds,
      changedFrontendFlagIds,
      changedWorkspaceFlagIds
    }),
    [changedLegacyFlagIds, changedFrontendFlagIds, changedWorkspaceFlagIds]
  );

  const sections = useMemo(() => {
    const built = buildFeatureFlagScopeSections(groups, workspaceColumns);
    return filterFeatureFlagScopeSections(built, search);
  }, [groups, workspaceColumns, search]);

  const selectedKeys = useMemo(
    () => (selectedScopeKey ? new Set([selectedScopeKey]) : new Set<string>()),
    [selectedScopeKey]
  );

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all' || keys.size === 0) {
      return;
    }
    const key = [...keys][0];
    if (key == null) {
      return;
    }
    onSelect(String(key) as FeatureFlagScopeKey);
  };

  const emptyMessage = search.trim()
    ? 'No scopes match.'
    : 'No scopes available.';

  return (
    <ScrollShadowWrapper
      renderTopShadow={false}
      className="min-h-0 flex-1 overflow-y-auto with-styled-scrollbars"
    >
      {sections.length === 0 ? (
        <p className="px-4 py-6 text-sm text-fg-muted">{emptyMessage}</p>
      ) : (
        <ListBox
          aria-label="Feature flag scopes"
          selectionMode="single"
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          className="divide-y-0"
        >
          {sections.map(section => (
            <Section key={section.name} id={section.name}>
              <Header
                className={cn(
                  '-header sticky top-0 z-[1] bg-bg-default px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted',
                  '[[data-has-top-shadow]_&]:shadow-sm'
                )}
              >
                {section.name}
              </Header>
              {section.scopes.map(scope => {
                const dirtyDot = isScopeDirty(scope.key, dirty);
                return (
                  <ListBoxItem
                    key={scope.key}
                    id={scope.key}
                    textValue={scope.label}
                    className="gap-3 px-3 py-3"
                  >
                    <ListBoxItemContent
                      showCheckmark={false}
                      icon={
                        dirtyDot ? (
                          <span
                            className="size-2 shrink-0 rounded-full bg-fg-attention"
                            aria-label="Unsaved changes"
                          />
                        ) : (
                          <span className="size-2 shrink-0" aria-hidden />
                        )
                      }
                      primary={scope.label}
                      extra={
                        scope.kind === 'legacy' ? (
                          <Building2
                            className="size-4 shrink-0 text-fg-muted"
                            aria-hidden
                          />
                        ) : scope.kind === 'frontend' ? (
                          <Monitor
                            className="size-4 shrink-0 text-fg-muted"
                            aria-hidden
                          />
                        ) : (
                          <EnterpriseWorkspaceIcon
                            name={scope.label}
                            iconUrl=""
                            size="sm"
                          />
                        )
                      }
                    />
                  </ListBoxItem>
                );
              })}
            </Section>
          ))}
        </ListBox>
      )}
    </ScrollShadowWrapper>
  );
}
